/* eslint-disable */
import React, { useState, useMemo, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Search, X, ChevronLeft } from 'lucide-react';
import { CheckPicker, SelectPicker } from 'rsuite';
import processFlowServices from '@/Services/Analytics Services/process-flow.services';
import { purchaseRequestServices } from '@/Services/PurchaseRequestServices/PurchaseRequestService';
import VariantTable from '@/Pages/Food Products/VariantPrice/VariantTable';
import { Input } from '@/Components/ui/input';
import { Button } from '@/Components/ui/button';
import { urls } from '@/Constants';
import { notificationServices } from '@/Services';
import { getHigherUnitName, getUnitInLowerUnit, useProductData } from '@/Helpers';
import { formActions, modalActions } from '@/Actions';
import { AlertModal } from '@/Components/Modals';


const columns = [
    {
        title: 'S.No',
        key: 'sno',
        width: 60,
        customCol: true,
    },
    {
        title: 'Product Name',
        key: 'productName',
        width: 300,
        customCol: true,
    },
    {
        title: 'SKU / Variant ID',
        key: 'skuCode',
        width: 200,
        customCol: true,
    },
    {
        title: 'Qty',
        key: 'qty',
        width: 120,
        customCol: true,
    },
    {
        title: 'Unit',
        key: 'unit',
        width: 100,
        customCol: true,
    },
    {
        title: 'Actions',
        key: 'actions',
        width: 80,
        customCol: true,
    },
];

function PurchaseRequestCreate() {
    const history = useHistory();
    const dispatch = useDispatch();

    const [formData, setFormData] = useState({
        selectedStoreId: '',
        selectedProcessId: '',
        prName: 'FG Items Apr 2026',
        selectedProducts: [],
    });

    const updateFormData = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const storeList = useSelector((state) => state.form?.FILTERED_STORES?.data?.LIST || []);
    const productList = useSelector((state) => state.form?.ALL_PRODUCTS?.data?.LIST || []);

    useEffect(() => {
        if (storeList?.length === 0) {
            dispatch(formActions.gateway({
                entity: 'FILTERED_STORES',
                req: {
                    params: {
                        id: '',
                    },
                },
                method: 'LIST',
            }));
        }
    }, []);

    useEffect(() => {
        if (productList?.length === 0) {
            useProductData(dispatch);
        }
    }, []);

    const storeOptions = useMemo(() =>
        storeList.map(store => ({ label: store.name, value: store.id?.toString() })),
        [storeList]);

    const { data: processNodes, isLoading: isLoadingProcesses } = useQuery({
        queryKey: ['processNodes', formData.selectedStoreId],
        queryFn: () => processFlowServices.fetchNodes({ params: { storeId: formData.selectedStoreId } }),
        enabled: !!formData.selectedStoreId,
    });

    const processOptions = useMemo(() => {
        const nodes = processNodes?.data?.nodes || [];
        return nodes.map(node => ({ label: `${node.data?.heading} - ${node.data?.sectionName}`, value: node.id?.toString() }));
    }, [processNodes]);

    const productOptions = useMemo(() => {
        const data = [];
        productList?.forEach((product) => {
            product?.variants?.forEach((variant) => {
                data.push({
                    label: `${product?.productName}(${variant?.displayName})${variant?.clientVariantId ? ` - ${variant?.clientVariantId}` : ''}`,
                    value: variant?.id,
                    variantId: variant?.id,
                    productId: product?.id,
                    productName: product?.productName,
                    displayName: variant?.displayName,
                    skuCode: variant?.clientVariantId || product?.skuCode,
                    unit: getHigherUnitName(product?.unit),
                    skuType: product?.skuType,
                });
            });
        });
        return data;
    }, [productList]);

    const { mutate: createPR, isLoading: isSubmitting } = useMutation({
        mutationFn: purchaseRequestServices.createPurchaseRequest,
        onSuccess: (res) => {
            if (res.statusCode === 200) {
                notificationServices.generateNotification({
                    type: 'success',
                    message: 'Purchase Request Created Successfully',
                });
                history.push(`${urls.EXTENSION}${urls.PURCHASE_REQUEST_LIST}`);
            } else {
                notificationServices.generateNotification({
                    type: 'error',
                    message: res.message || 'Something went wrong',
                });
            }
        },
        onError: (err) => {
            notificationServices.generateNotification({
                type: 'error',
                message: err.message || 'Error creating PR',
            });
        }
    });

    const handleAddProduct = (selectedIds) => {
        const newSelectedProducts = selectedIds.map(id => {
            const existing = formData.selectedProducts.find(p => p.variantId === id);
            if (existing) return existing;

            const option = productOptions.find(opt => opt.value === id);
            return {
                ...option,
                id: option.variantId,
                qty: 0,
            };
        });
        updateFormData('selectedProducts', newSelectedProducts);
    };

    const handleRemoveProduct = (productId) => {
        updateFormData('selectedProducts', formData.selectedProducts.filter((p) => p.id !== productId));
    };

    const handleQtyChange = (productId, newQty) => {
        updateFormData('selectedProducts',
            formData.selectedProducts.map((p) =>
                p.id === productId ? { ...p, qty: newQty } : p
            )
        );
    };

    const customRowData = {
        sno: (row, index) => index + 1,
        productName: (row) => (
            <div className='flex flex-col'>
                <span className='font-medium text-gray-800'>{row.productName}</span>
                <span className='text-[10px] text-gray-400'>{row.displayName}</span>
            </div>
        ),
        skuCode: (row) => <span className='bg-gray-100 px-2 py-1 rounded text-xs font-mono'>{row.skuCode || 'N/A'}</span>,
        qty: (row) => {
            const isCountOrPcs = ['count', 'pcs'].includes(row.unit?.toLowerCase());
            const isDecimal = row.qty && Number(row.qty) % 1 !== 0;
            const hasError = isCountOrPcs && isDecimal;

            return (
                <div className='flex flex-col items-center'>
                    <Input
                        type='number'
                        value={row.qty}
                        onChange={(e) => handleQtyChange(row.id, e.target.value)}
                        className={`w-20 text-center h-8 text-sm ${hasError ? 'border-red-500 ring-red-500' : ''}`}
                    />
                    {hasError ? (
                        <span className='text-[9px] text-red-500 mt-1 leading-tight text-center font-bold'>
                            Can't enter in decimal
                        </span>
                    ) : null}
                </div>
            );
        },
        unit: (row) => <span className='text-sm text-gray-600'>{row.unit || 'Nos'}</span>,
        actions: (row) => (
            <Button
                variant='ghost'
                size='icon'
                onClick={() => handleRemoveProduct(row.id)}
                className='text-gray-400 hover:text-red-500 h-8 w-8'
            >
                <X size={16} />
            </Button>
        ),
    };

    const handlePRSubmit = () => {
        if (!formData.selectedStoreId) {
            notificationServices.generateNotification({ type: 'error', message: 'Please select a store' });
            return;
        }
        if (formData.selectedProducts.length === 0) {
            notificationServices.generateNotification({ type: 'error', message: 'Please add at least one product' });
            return;
        }

        // Decimal validation check
        const hasDecimalError = formData.selectedProducts.some(p => {
            const isCountOrPcs = ['count', 'pcs'].includes(p.unit?.toLowerCase());
            const isDecimal = p.qty && Number(p.qty) % 1 !== 0;
            return isCountOrPcs && isDecimal;
        });

        if (hasDecimalError) {
            notificationServices.generateNotification({
                type: 'error',
                message: 'Some products with unit Count/Pcs have decimal quantities. Please correct them.'
            });
            return;
        }

        dispatch(modalActions.toggleAlert(true, {
            heading: 'Confirm Submission',
            message: 'Are you sure you want to submit this Purchase Request?',
            confirmText: 'Yes, Submit',
            cancelText: 'Cancel',
            handleConfirm: () => {
                dispatch(modalActions.toggleAlert(false));

                const payload = {
                    data: {
                        type: "PURCHASE_ORDER",
                        storeId: Number(formData.selectedStoreId) || null,
                        processId: Number(formData.selectedProcessId) || null,
                        description: formData.prName,
                        items: formData.selectedProducts.map(p => ({
                            entityId: Number(p.variantId),
                            qty: getUnitInLowerUnit(p?.unit, parseFloat(p.qty)),
                        })),
                    }
                };
                createPR(payload);
            }
        }));
    };

    const [tableSearch, setTableSearch] = useState('');

    const filteredTableData = useMemo(() => {
        if (!tableSearch) return formData.selectedProducts;
        return formData.selectedProducts.filter(p =>
            p.productName?.toLowerCase().includes(tableSearch.toLowerCase()) ||
            p.skuCode?.toLowerCase().includes(tableSearch.toLowerCase())
        );
    }, [formData.selectedProducts, tableSearch]);

    const handleCustomCol = useMemo(() => (column) => {
        return (
            <span className='text-[16px] text-gray-500 uppercase tracking-tight'>
                {column.title}
            </span>
        );
    }, []);

    return (
        <div className='max-w-[1400px] mx-auto bg-white rounded-lg shadow-sm p-4'>
            {/* Header */}
            <AlertModal />
            <div className='flex justify-between items-center mb-4 border-b border-gray-100 pb-3'>
                <div className='flex items-center gap-3'>
                    <Button
                        variant='ghost'
                        size='icon'
                        className='h-8 w-8 hover:bg-gray-100'
                        onClick={() => history.push(`${urls.EXTENSION}${urls.PURCHASE_REQUEST_LIST}`)}
                    >
                        <ChevronLeft size={20} className='text-gray-600' />
                    </Button>
                    <h1 className='text-lg font-bold text-gray-800'>Raise Purchase Request</h1>
                </div>
                <Button
                    className='bg-[#1e1e1e] hover:bg-black text-white px-5 h-9 text-sm'
                    onClick={handlePRSubmit}
                    disabled={isSubmitting}
                >
                    {isSubmitting ? 'Submitting...' : 'Submit PR'}
                </Button>
            </div>

            {/* Selection Form */}
            <div className='grid grid-cols-1 md:grid-cols-4 gap-x-4 gap-y-3 mb-4 bg-gray-50/50 p-3 rounded-md border border-gray-100'>
                <div className='space-y-1 flex flex-col'>
                    <label className='text-[11px] font-bold text-red-600 uppercase tracking-tight'>Purchase From Store *</label>
                    <SelectPicker
                        data={storeOptions}
                        value={formData.selectedStoreId}
                        onChange={(val) => updateFormData('selectedStoreId', val)}
                        placeholder='Select Store'
                        block
                        size='sm'
                        menuStyle={{ zIndex: 9999 }}
                    />
                </div>

                <div className='space-y-1 flex flex-col'>
                    <label className='text-[11px] font-bold text-gray-500 uppercase tracking-tight'>Select Process</label>
                    <SelectPicker
                        data={processOptions}
                        value={formData.selectedProcessId}
                        onChange={(val) => updateFormData('selectedProcessId', val)}
                        placeholder={isLoadingProcesses ? 'Loading...' : 'Select Process'}
                        disabled={!formData.selectedStoreId || isLoadingProcesses}
                        block
                        size='sm'
                        menuStyle={{ zIndex: 9999 }}
                    />
                </div>

                <div className='space-y-1 flex flex-col'>
                    <label className='text-[11px] font-bold text-gray-500 uppercase tracking-tight'>PR Name</label>
                    <Input
                        value={formData.prName}
                        onChange={(e) => updateFormData('prName', e.target.value)}
                        placeholder='Enter PR Name'
                        className='h-[30px] text-sm'
                    />
                </div>

                <div className='space-y-1 flex flex-col'>
                    <label className='text-[11px] font-bold text-gray-500 uppercase tracking-tight'>PR Date</label>
                    <div className='text-sm font-bold text-gray-700 py-1'>{new Date().toISOString().split('T')[0]}</div>
                </div>
            </div>

            {/* Product Selection & Table Search */}
            <div className='mb-4 bg-white border border-gray-100 p-3 rounded-md shadow-sm'>
                <div className='flex items-center justify-between gap-4'>
                    <div className='flex items-center gap-3 flex-1'>
                        <span className='text-sm font-bold text-gray-700 whitespace-nowrap'>Add Products:</span>
                        <CheckPicker
                            data={productOptions}
                            value={formData.selectedProducts.map(p => p.variantId)}
                            onChange={handleAddProduct}
                            placeholder='Search & Add Products...'
                            style={{ width: '600px' }}
                            cleanable={false}
                            size='sm'
                            menuStyle={{ zIndex: 9999 }}
                        />
                    </div>
                    <div className='flex items-center gap-2 bg-white px-3 py-1.5 rounded-md border border-gray-300 shadow-sm transition-all focus-within:border-blue-400 focus-within:ring-1 focus-within:ring-blue-100'>
                        <Search size={14} className='text-gray-500' />
                        <input
                            type='text'
                            value={tableSearch}
                            onChange={(e) => setTableSearch(e.target.value)}
                            placeholder='Search ...'
                            className='bg-transparent border-none outline-none text-sm w-56 placeholder:text-gray-400 text-gray-700'
                        />
                        {tableSearch && (
                            <X
                                size={14}
                                className='text-gray-400 cursor-pointer hover:text-red-500'
                                onClick={() => setTableSearch('')}
                            />
                        )}
                    </div>
                </div>
            </div>

            {/* Product Table */}
            <div className='border border-gray-200 rounded-md max-h-[calc(100vh-300px)] shadow-sm'>
                <VariantTable
                    data={filteredTableData}
                    columns={columns}
                    customRowData={customRowData}
                    customCol={handleCustomCol}
                    customInfoLine='No products added yet. Use the select picker above to add products to your PR.'
                />
            </div>
        </div>
    );
}

export default PurchaseRequestCreate;
